import React, { useState } from 'react'
import { Button, Modal } from './ui'
import { CoreTeamFormModalMetaProps, Stream, Volunteer } from '../data/types'

type Props = {
    meta: CoreTeamFormModalMetaProps
    isOpen: boolean
    volunteer: Volunteer | null
    currentUserName?: string
    userHasManagePermission: boolean
    userApprovedStreams?: Stream[]
    onClose: () => void
    onSubmit: (data: { volunteer_id: number, streams: Stream[], role: string, tier: number, notes?: string, status?: 'active' | 'on hold' }) => Promise<void>
    initialData?: {
        streams: Stream[]
        role: string
        tier: number
        notes?: string
        status?: 'active' | 'on hold'
    }
}

const STREAM_OPTIONS: Stream[] = [
    'Commercial',
    'Comms',
    'External Venue',
    'Finance',
    'HR',
    'Intezaar',
    'Ismaili Centre',
    'Jamati Experience',
    'Medical',
    'NCF',
    'Registration',
    'Safety & Security'
]

export const CoreTeamFormModal = (props: Props) => {
    const { meta, isOpen, volunteer, currentUserName, userHasManagePermission, userApprovedStreams, onClose, onSubmit, initialData } = props

    // Filter streams based on user permissions
    const availableStreams = userHasManagePermission
        ? STREAM_OPTIONS
        : (userApprovedStreams && userApprovedStreams.length > 0 ? userApprovedStreams : [])

    const [streams, setStreams] = useState<Stream[]>(initialData?.streams || [])
    const [role, setRole] = useState(initialData?.role || '')
    const [tier, setTier] = useState<number>(initialData?.tier || 5)
    const [status, setStatus] = useState<'active' | 'on hold'>(initialData?.status || 'on hold')
    const [notes, setNotes] = useState(initialData?.notes || '')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Update state when initialData changes (for edit mode)
    React.useEffect(() => {
        if (initialData) {
            setStreams(initialData.streams)
            setRole(initialData.role)
            setTier(initialData.tier)
            setStatus(initialData.status || 'active')
            setNotes(initialData.notes || '')
        }
    }, [initialData])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!volunteer) return

        if (streams.length === 0) {
            setError(meta.streamsRequiredError)
            return
        }

        if (!role.trim()) {
            setError(meta.roleRequiredError)
            return
        }

        if (tier === undefined || tier < 0 || tier > 5) {
            setError(meta.tierRequiredError)
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            const submitData: { volunteer_id: number, streams: Stream[], role: string, tier: number, notes?: string, status?: 'active' | 'on hold' } = {
                volunteer_id: volunteer.csid,
                streams,
                role: role.trim(),
                tier,
                notes: notes.trim() || undefined,
                status
            }

            await onSubmit(submitData)
            handleClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setStreams([])
        setRole('')
        setTier(1)
        setStatus('on hold')
        setNotes('')
        setError(null)
        onClose()
    }

    const toggleStream = (stream: Stream) => {
        setStreams(prev =>
            prev.includes(stream)
                ? prev.filter(s => s !== stream)
                : [...prev, stream]
        )
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={meta.title}
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                {volunteer && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{meta.volunteerLabel}:</span>{' '}
                            {volunteer.first_name} {volunteer.last_name}
                        </p>
                        {currentUserName && (
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                <span className="font-medium">{meta.submittedByLabel}:</span>{' '}
                                {currentUserName}
                            </p>
                        )}
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {meta.streamsLabel} *
                    </label>
                    <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                        {availableStreams.map(stream => (
                            <label
                                key={stream}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded"
                            >
                                <input
                                    type="checkbox"
                                    checked={streams.includes(stream)}
                                    onChange={() => toggleStream(stream)}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-200">
                                    {stream}
                                </span>
                            </label>
                        ))}
                    </div>
                    {streams.length > 0 && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {streams.length} {meta.streamsSelectedLabel}
                        </p>
                    )}
                </div>

                <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {meta.roleLabel} *
                    </label>
                    <input
                        type="text"
                        id="role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        placeholder={meta.rolePlaceholder}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        disabled={isSubmitting}
                    />
                </div>

                <div>
                    <label htmlFor="tier" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {meta.tierLabel} *
                    </label>
                    <select
                        id="tier"
                        value={tier}
                        onChange={(e) => setTier(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        disabled={isSubmitting}
                    >
                        <option value={0}>Tier 0</option>
                        <option value={1}>Tier 1</option>
                        <option value={2}>Tier 2</option>
                        <option value={3}>Tier 3</option>
                        <option value={4}>Tier 4</option>
                        <option value={5}>Reserved</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {meta.statusLabel}
                    </label>
                    <select
                        id="status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as 'active' | 'on hold')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        disabled={isSubmitting}
                    >
                        <option value="active">{meta.activeStatus}</option>
                        <option value="on hold">{meta.onHoldStatus}</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {meta.notesLabel}
                    </label>
                    <textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={meta.notesPlaceholder}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        disabled={isSubmitting}
                    />
                </div>

                {error && (
                    <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}
            </form>
        </Modal>
    )
}
